import api from '../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

/**
 * QuickBooks Finance Service - API client for QuickBooks financial data
 * Handles multi-entity financial views, intercompany tracking, and reconciliation
 */

// ============================================================================
// ENTITIES (QuickBooks Companies)
// ============================================================================

export const getQuickBooksEntities = async () => {
  const response = await api.get(`${API_BASE}/api/quickbooks/entities`);
  return response.data;
};

// ============================================================================
// FINANCIAL OVERVIEW
// ============================================================================

export const getFinancialOverview = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/overview`, { params });
  return response.data;
};

export const getCashPosition = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/cash-position`, { params });
  return response.data;
};

export const getBalanceSheetSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/balance-sheet`, { params });
  return response.data;
};

export const getProfitLossSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/profit-loss`, { params });
  return response.data;
};

// ============================================================================
// ACCOUNTS RECEIVABLE
// ============================================================================

export const getARAgingSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/ar-aging`, { params });
  return response.data;
};

export const getOutstandingInvoices = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/invoices/outstanding`, { params });
  return response.data;
};

export const getCustomerBalances = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/customers/balances`, { params });
  return response.data;
};

// ============================================================================
// ACCOUNTS PAYABLE
// ============================================================================

export const getAPAgingSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/ap-aging`, { params });
  return response.data;
};

export const getOutstandingBills = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/bills/outstanding`, { params });
  return response.data;
};

export const getVendorBalances = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/vendors/balances`, { params });
  return response.data;
};

// ============================================================================
// INTERCOMPANY TRANSACTIONS
// ============================================================================

export const getIntercompanyTransactions = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/intercompany`, { params });
  return response.data;
};

export const getIntercompanyBalances = async () => {
  const response = await api.get(`${API_BASE}/api/quickbooks/intercompany/balances`);
  return response.data;
};

// ============================================================================
// RECONCILIATION & MISMATCHES
// ============================================================================

export const getReconciliationSummary = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/reconciliation`, { params });
  return response.data;
};

export const getBillPaymentMismatches = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/reconciliation/bill-payments`, { params });
  return response.data;
};

export const getInvoicePaymentMismatches = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/reconciliation/invoice-payments`, { params });
  return response.data;
};

// ============================================================================
// GENERAL LEDGER
// ============================================================================

export const getGeneralLedger = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/general-ledger`, { params });
  return response.data;
};

export const getAccountTransactions = async (accountId, params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/accounts/${accountId}/transactions`, { params });
  return response.data;
};

// ============================================================================
// REPORTS
// ============================================================================

export const getCashFlowReport = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/reports/cash-flow`, { params });
  return response.data;
};

export const getMonthlyTrends = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/reports/trends`, { params });
  return response.data;
};

// ============================================================================
// AI-POWERED ANALYSIS
// ============================================================================

export const getFinancialAnomalies = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/anomalies`, { params });
  return response.data;
};

export const getAIAnalysis = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/ai-analysis`, { params });
  return response.data;
};

// ============================================================================
// REAL-TIME QUICKBOOKS API FUNCTIONS
// ============================================================================

/**
 * Fetch live bank balances directly from QuickBooks API
 * Bypasses cached data for real-time accuracy
 */
export const getLiveBankBalances = async (params = {}) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/live/bank-balances`, { params });
  return response.data;
};

/**
 * Fetch live balance sheet from QuickBooks API
 */
export const getLiveBalanceSheet = async (connectorId) => {
  const response = await api.get(`${API_BASE}/api/quickbooks/live/balance-sheet`, {
    params: { connector_id: connectorId }
  });
  return response.data;
};

/**
 * Get data freshness information
 */
export const getDataFreshness = async () => {
  const response = await api.get(`${API_BASE}/api/quickbooks/data-freshness`);
  return response.data;
};

/**
 * Stream AI analysis with real-time updates using Server-Sent Events
 * @param {Object} params - Query parameters (connector_id optional)
 * @param {Function} onStatus - Callback for status updates
 * @param {Function} onAnomalies - Callback when anomalies data is received
 * @param {Function} onAnalysis - Callback when AI analysis is received
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when streaming completes
 * @returns {EventSource} - The event source instance (can be closed to cancel)
 */
export const streamAIAnalysis = (params = {}, callbacks = {}) => {
  const { onStatus, onAnomalies, onAnalysis, onError, onComplete } = callbacks;

  const queryString = new URLSearchParams(params).toString();
  const url = `${API_BASE}/api/quickbooks/ai-analysis/stream${queryString ? '?' + queryString : ''}`;

  // Create EventSource with credentials
  const eventSource = new EventSource(url, { withCredentials: true });

  eventSource.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'status':
          onStatus?.(data.message);
          break;
        case 'anomalies':
          onAnomalies?.(data.data);
          break;
        case 'ai_analysis':
          onAnalysis?.(data.data);
          break;
        case 'error':
          onError?.(data.message);
          break;
        case 'complete':
          onComplete?.(data.generated_at);
          eventSource.close();
          break;
        default:
          console.warn('Unknown SSE event type:', data.type);
      }
    } catch (e) {
      console.error('Error parsing SSE data:', e);
      onError?.('Error parsing server response');
    }
  };

  eventSource.onerror = (error) => {
    console.error('SSE connection error:', error);
    onError?.('Connection to AI analysis stream failed');
    eventSource.close();
  };

  return eventSource;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Map currency names to ISO codes
 */
const CURRENCY_CODE_MAP = {
  'Australian Dollar': 'AUD',
  'British Pound Sterling': 'GBP',
  'United States Dollar': 'USD',
  'New Zealand Dollar': 'NZD',
  'Euro': 'EUR',
  'Canadian Dollar': 'CAD',
  'Singapore Dollar': 'SGD',
  'Hong Kong Dollar': 'HKD',
  'Japanese Yen': 'JPY',
  'Chinese Yuan': 'CNY',
  'Indian Rupee': 'INR',
  'AUD': 'AUD',
  'GBP': 'GBP',
  'USD': 'USD',
  'NZD': 'NZD',
  'EUR': 'EUR',
  'CAD': 'CAD',
  'SGD': 'SGD',
  'HKD': 'HKD',
  'JPY': 'JPY',
  'CNY': 'CNY',
  'INR': 'INR',
};

const getCurrencyCode = (currency) => {
  if (!currency) return 'GBP';
  if (CURRENCY_CODE_MAP[currency]) return CURRENCY_CODE_MAP[currency];
  // A 3-letter ISO code outside the name map must pass through as itself —
  // defaulting it to GBP would render another currency's amount with a £ symbol.
  const code = String(currency).trim().toUpperCase();
  if (/^[A-Z]{3}$/.test(code)) return code;
  return 'GBP';
};

/**
 * Format currency value with proper symbol
 */
export const formatCurrency = (value, currency = 'GBP') => {
  if (value === null || value === undefined) return '-';
  const currencyCode = getCurrencyCode(currency);
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  } catch (e) {
    return `${currencyCode} ${Math.round(value)}`;
  }
};

/**
 * Format currency with decimals
 */
export const formatCurrencyPrecise = (value, currency = 'GBP') => {
  if (value === null || value === undefined) return '-';
  const currencyCode = getCurrencyCode(currency);
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch (e) {
    // Fallback if currency code is invalid
    return `${currencyCode} ${value.toFixed(2)}`;
  }
};

const CURRENCY_SYMBOLS = {
  GBP: '£', USD: '$', EUR: '€', AUD: 'A$', CAD: 'C$', NZD: 'NZ$',
  SGD: 'S$', HKD: 'HK$', JPY: '¥', CNY: '¥', INR: '₹', CHF: 'CHF ',
};
const currencySymbol = (currency) => CURRENCY_SYMBOLS[getCurrencyCode(currency)] || `${getCurrencyCode(currency)} `;

/**
 * Format large numbers with K/M suffix in the given currency's symbol (no hardcoded £).
 */
export const formatCompactCurrency = (value, currency = 'GBP') => {
  if (value === null || value === undefined) return '-';
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const sym = currencySymbol(currency);

  if (absValue >= 1000000) {
    return `${sign}${sym}${(absValue / 1000000).toFixed(1)}M`;
  } else if (absValue >= 1000) {
    return `${sign}${sym}${(absValue / 1000).toFixed(0)}K`;
  }
  return formatCurrency(value, currency);
};

/**
 * Format number with commas
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-GB').format(value);
};

/**
 * Format date for display (YYYY-MM-DD format)
 */
export const formatDate = (dateString) => {
  if (!dateString || dateString === '1970-01-01') return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format relative date (e.g., "3 days ago")
 */
export const formatRelativeDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  return formatDate(dateString);
};

/**
 * Calculate days overdue
 */
export const calculateDaysOverdue = (dueDate) => {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  const now = new Date();
  return Math.floor((now - due) / (1000 * 60 * 60 * 24));
};

/**
 * Get color for aging bucket
 */
export const getAgingColor = (daysOverdue) => {
  if (daysOverdue <= 0) return 'success';
  if (daysOverdue <= 30) return 'info';
  if (daysOverdue <= 60) return 'warning';
  return 'error';
};

/**
 * Get entity color for visual distinction
 */
export const getEntityColor = (entityName) => {
  const colors = {
    'Excite Foundry Ltd': '#3b82f6',
    'Life O Pty Ltd': '#10b981',
    'Excite Foundry Inc': '#8b5cf6',
    'Life O Inc': '#f59e0b',
    'Reach Lifestyle Ltd': '#ef4444',
    "Nature's Organics Products Inc": '#ec4899'
  };
  return colors[entityName] || '#6b7280';
};

export default {
  // Entities
  getQuickBooksEntities,
  // Overview
  getFinancialOverview,
  getCashPosition,
  getBalanceSheetSummary,
  getProfitLossSummary,
  // AR
  getARAgingSummary,
  getOutstandingInvoices,
  getCustomerBalances,
  // AP
  getAPAgingSummary,
  getOutstandingBills,
  getVendorBalances,
  // Intercompany
  getIntercompanyTransactions,
  getIntercompanyBalances,
  // Reconciliation
  getReconciliationSummary,
  getBillPaymentMismatches,
  getInvoicePaymentMismatches,
  // GL
  getGeneralLedger,
  getAccountTransactions,
  // Reports
  getCashFlowReport,
  getMonthlyTrends,
  // AI Analysis
  getFinancialAnomalies,
  getAIAnalysis,
  streamAIAnalysis,
  // Real-time QuickBooks API
  getLiveBankBalances,
  getLiveBalanceSheet,
  getDataFreshness,
  // Helpers
  formatCurrency,
  formatCurrencyPrecise,
  formatCompactCurrency,
  formatNumber,
  formatDate,
  formatRelativeDate,
  calculateDaysOverdue,
  getAgingColor,
  getEntityColor
};
